import type { ReactNode } from 'react';

/**
 * Hand-rolled SVG charts.
 *
 * Deliberately not a charting library: every chart here is a single series, and
 * recharts would add ~100KB gzipped to a bundle currently under 80KB for shapes
 * we can draw in a few lines. It also lets the marks follow the house rules
 * exactly — thin marks, 4px rounded data-ends, recessive grid, tooltip on hover.
 *
 * Colour: one hue per chart (magnitude and trend are both single-series jobs),
 * taken from the app's accent token so it swaps correctly between light and
 * dark. Delta indicators use the reserved status colours and always ship with
 * an arrow and a number — never colour alone.
 */

/* ---------------------------- horizontal bars ---------------------------- */

export interface BarDatum {
  label: string;
  value: number;
  /** Optional identity dot — e.g. the admin-configured status colour. */
  dotColor?: string;
}

/**
 * Magnitude comparison across many named categories.
 *
 * Horizontal because the labels are words, not dates. One hue: length carries
 * magnitude, and the optional dot carries identity, so hue never has to do two
 * jobs at once.
 */
export function BarList({ data, total }: { data: BarDatum[]; total?: number }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  const sum = total ?? data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="bar-list">
      {data.map((d) => {
        const pct = sum > 0 ? (d.value / sum) * 100 : 0;
        return (
          <div className="bar-row" key={d.label} title={`${d.label}: ${d.value} (${pct.toFixed(1)}%)`}>
            <div className="bar-label">
              {d.dotColor && <span className="bar-dot" style={{ background: d.dotColor }} />}
              <span className="truncate">{d.label}</span>
            </div>
            <div className="bar-track">
              <div
                className="bar-fill"
                style={{ width: `${(d.value / max) * 100}%` }}
                role="img"
                aria-label={`${d.value}`}
              />
            </div>
            <div className="bar-value num">
              {d.value}
              <span className="bar-pct">{pct.toFixed(0)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------- stat tile -------------------------------- */

export function StatTile({
  label,
  value,
  hint,
  delta,
  tone,
  icon,
}: {
  label: string;
  value: string | number;
  hint?: string;
  /** Percentage change vs the comparison period. */
  delta?: { pct: number; goodWhenUp: boolean } | null;
  tone?: 'default' | 'warning';
  /** Small badge in the tile's corner — purely decorative, never the only signal. */
  icon?: ReactNode;
}) {
  return (
    <div className={`stat-tile${tone === 'warning' ? ' stat-warning' : ''}`}>
      <div className="stat-head">
        <div className="stat-label">{label}</div>
        {icon && <div className="stat-icon" aria-hidden="true">{icon}</div>}
      </div>
      <div className="stat-value">{typeof value === 'number' ? value.toLocaleString() : value}</div>
      <div className="stat-foot">
        {delta && Number.isFinite(delta.pct) && (
          <Delta pct={delta.pct} goodWhenUp={delta.goodWhenUp} />
        )}
        {hint && <span className="stat-hint">{hint}</span>}
      </div>
    </div>
  );
}

/**
 * Change indicator.
 *
 * Colour never carries this alone — there is always an arrow and a number, so
 * it survives colourblindness, greyscale printing and forced-colors mode.
 */
export function Delta({ pct, goodWhenUp }: { pct: number; goodWhenUp: boolean }) {
  if (!Number.isFinite(pct)) return null;
  const flat = Math.abs(pct) < 0.5;
  const up = pct > 0;
  const good = flat ? null : up === goodWhenUp;

  return (
    <span
      className="delta"
      style={{ color: good === null ? 'var(--muted)' : good ? 'var(--chart-good)' : 'var(--chart-bad)' }}
    >
      <span aria-hidden="true">{flat ? '→' : up ? '↑' : '↓'}</span>
      {flat ? 'no change' : `${Math.abs(pct).toFixed(0)}%`}
    </span>
  );
}

export function percentChange(current: number, previous: number): number {
  if (previous === 0) return current === 0 ? 0 : 100;
  return ((current - previous) / previous) * 100;
}
